/**
 * MiniProjectMilestoneList — Task #227.
 *
 * FP-style milestone table for the MP Summary sub-tab. Matches the
 * mockup Skip approved on 2026-05-27:
 *   - Section header with "MILESTONES" eyebrow + "+" button (Lead only)
 *   - Table columns: status icon | Name | single date | actions
 *   - Status derivation client-side:
 *       met      — actualDate IS NOT NULL  (blue circle)
 *       overdue  — targetDate < today AND actualDate IS NULL  (red circle)
 *       upcoming — everything else  (green circle)
 *   - One color-coded date column (no T:/A: labels):
 *       blue  — actual date (milestone met)
 *       red   — target past, no actual (overdue)
 *       green — target in the future (upcoming)
 *
 * Uses the polymorphic /api/projectmilestones endpoints with the MP
 * helpers from api/projectMilestones.ts. Lead-only create/edit/delete
 * via the right-edge MiniProjectMilestoneEditTray (replaced the inline
 * AddRow + EditRow on 2026-05-27 — narrow magazine-column widths made
 * the inline forms hard to lay out).
 *
 * Refresh: listens for EVT_MINI_PROJECT_MUTATED so edits made elsewhere
 * (or from the tray) repaint without a page reload.
 *
 * Not implemented in this pass:
 *   - Drag-to-reorder. Sort order is status-then-target-date today.
 */

import { useCallback, useEffect, useState } from "react";
import {
  fetchMiniProjectMilestones,
  type ProjectMilestoneResponse,
} from "../../api/projectMilestones";
import { EVT_MINI_PROJECT_MUTATED } from "../../api/miniProjects";
import { getActiveIndicatorPalette } from "../../theme/colorBlindMode";
import MiniProjectMilestoneEditTray from "./MiniProjectMilestoneEditTray";

interface Props {
  miniProjectGuid: string;
  canEdit: boolean;
}

type Status = "met" | "overdue" | "upcoming";

function deriveStatus(m: ProjectMilestoneResponse): Status {
  if (m.actualDate) return "met";
  if (m.targetDate && m.targetDate < todayIso()) return "overdue";
  return "upcoming";
}

/** Read-only date cell: prefer actual (met → blue), else target.
 *  Falls back to "—" when neither is set. */
function pickDisplayDate(m: ProjectMilestoneResponse, status: Status): string {
  if (status === "met") return fmtDate(m.actualDate);
  if (m.targetDate) return fmtDate(m.targetDate);
  return "—";
}

/** Color style for the single-date cell, by status. */
function dateCellStyle(
  m: ProjectMilestoneResponse,
  status: Status,
): { color: string } {
  const p = getActiveIndicatorPalette();
  if (status === "met") return { color: p.completed };
  if (status === "overdue") return { color: p.red };
  if (status === "upcoming" && m.targetDate) return { color: p.green };
  return { color: "var(--text-tertiary)" };
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/* ── Tray state shape ───────────────────────────────────────
 * Discriminated union so the tray remounts cleanly on each open
 * (useState initializers in the tray re-fire on key change). The
 * `nonce` is a per-open timestamp so consecutive +clicks (and re-opens
 * after a successful save) yield fresh blank state rather than reusing
 * the previous render. */
type TrayState =
  | { kind: "closed" }
  | { kind: "create"; nonce: number }
  | { kind: "edit"; milestone: ProjectMilestoneResponse };

export default function MiniProjectMilestoneList({
  miniProjectGuid,
  canEdit,
}: Props) {
  const [milestones, setMilestones] = useState<ProjectMilestoneResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tray, setTray] = useState<TrayState>({ kind: "closed" });

  const reload = useCallback(async () => {
    if (!miniProjectGuid) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchMiniProjectMilestones(miniProjectGuid);
      setMilestones(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [miniProjectGuid]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Refetch on MP mutations from other surfaces (Task #221 broadcast).
  useEffect(() => {
    const onMutated = (e: Event) => {
      const detail = (e as CustomEvent<{ miniProjectGuid?: string }>).detail;
      if (!detail?.miniProjectGuid) {
        reload();
        return;
      }
      if (detail.miniProjectGuid === miniProjectGuid) reload();
    };
    if (typeof window !== "undefined") {
      window.addEventListener(EVT_MINI_PROJECT_MUTATED, onMutated as any);
      return () =>
        window.removeEventListener(EVT_MINI_PROJECT_MUTATED, onMutated as any);
    }
    return undefined;
  }, [miniProjectGuid, reload]);

  // ── Tray open/close helpers ──
  // `nonce: Date.now()` gives the create-tray a fresh key on each open so
  // useState initializers inside the tray re-fire and the form starts
  // blank. (Edit uses milestone.id as its key for the same reason.)
  const openCreate = useCallback(() => {
    setTray({ kind: "create", nonce: Date.now() });
  }, []);

  const openEdit = useCallback((m: ProjectMilestoneResponse) => {
    setTray({ kind: "edit", milestone: m });
  }, []);

  const closeTray = useCallback(() => {
    setTray({ kind: "closed" });
  }, []);

  // After a successful tray mutation (create / edit / delete):
  //   - close the tray
  //   - reload locally (the API helpers also dispatch EVT_MINI_PROJECT_MUTATED
  //     which will trigger a reload via the listener above — but we call
  //     reload() directly for immediate feedback and to handle delete,
  //     which has no broadcast from the API helper itself).
  const handleMutated = useCallback(() => {
    setTray({ kind: "closed" });
    reload();
  }, [reload]);

  return (
    <div
      style={{
        background: "var(--bg-surface)",
        border: "1px solid var(--border-default)",
        borderRadius: 8,
        padding: "12px 14px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBlockEnd: 10,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: 0.5,
            color: "var(--text-muted)",
            textTransform: "uppercase",
          }}
        >
          Milestones
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={openCreate}
            aria-label="Add milestone"
            style={{
              inlineSize: 22,
              blockSize: 22,
              padding: 0,
              borderRadius: 4,
              border: "1px solid var(--border-default)",
              background: "transparent",
              color: "var(--text-primary)",
              cursor: "pointer",
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            +
          </button>
        )}
      </div>

      {error && (
        <div
          style={{
            background: "var(--bg-surface)",
            border: "1px solid var(--color-error)",
            borderRadius: 6,
            padding: "6px 10px",
            color: "var(--color-error)",
            fontSize: 12,
            marginBlockEnd: 8,
          }}
        >
          {error}
        </div>
      )}

      {loading && milestones.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "8px 0" }}>
          Loading…
        </div>
      ) : milestones.length === 0 ? (
        <div
          style={{
            fontSize: 12,
            color: "var(--text-muted)",
            padding: "12px 0",
            fontStyle: "italic",
          }}
        >
          No milestones yet.{canEdit ? " Click + to add one." : ""}
        </div>
      ) : (
        <MilestoneTable
          milestones={milestones}
          canEdit={canEdit}
          onEdit={openEdit}
        />
      )}

      {/* Tray — keyed so useState initializers fire fresh on each open.
          Create uses the counter; edit uses the milestone id. */}
      {tray.kind === "create" && (
        <MiniProjectMilestoneEditTray
          key={`create-${tray.nonce}`}
          mode="create"
          open
          miniProjectGuid={miniProjectGuid}
          onClose={closeTray}
          onMutated={handleMutated}
        />
      )}
      {tray.kind === "edit" && (
        <MiniProjectMilestoneEditTray
          key={`edit-${tray.milestone.id}`}
          mode="edit"
          open
          milestone={tray.milestone}
          miniProjectGuid={miniProjectGuid}
          onClose={closeTray}
          onMutated={handleMutated}
        />
      )}
    </div>
  );
}

/* ── Table ──────────────────────────────────────────────── */

function MilestoneTable({
  milestones,
  canEdit,
  onEdit,
}: {
  milestones: ProjectMilestoneResponse[];
  canEdit: boolean;
  onEdit: (m: ProjectMilestoneResponse) => void;
}) {
  // Sort: overdue first, then upcoming by target date, then met by actual date.
  const sorted = [...milestones].sort((a, b) => {
    const orderRank: Record<Status, number> = { overdue: 0, upcoming: 1, met: 2 };
    const sa = deriveStatus(a);
    const sb = deriveStatus(b);
    if (sa !== sb) return orderRank[sa] - orderRank[sb];
    // Within the same status, order by target date (nulls last).
    const ta = a.targetDate ?? "9999-12-31";
    const tb = b.targetDate ?? "9999-12-31";
    return ta.localeCompare(tb);
  });

  return (
    <table
      style={{
        inlineSize: "100%",
        fontSize: 12,
        borderCollapse: "collapse",
        tableLayout: "fixed",
      }}
    >
      {/* No column header row — the section eyebrow already says
          MILESTONES, so a redundant "Milestone" label just adds noise.
          The status circle, name, and color-coded date column are
          self-explanatory at a glance. <colgroup> preserves the column
          widths the <thead> used to enforce under tableLayout: fixed. */}
      <colgroup>
        <col style={{ inlineSize: 20 }} />
        <col />
        <col style={{ inlineSize: 88 }} />
        {canEdit && <col style={{ inlineSize: 36 }} />}
      </colgroup>
      <tbody>
        {sorted.map((m) => {
          const status = deriveStatus(m);
          return (
            <tr
              key={m.id}
              style={{ borderBlockStart: "1px solid var(--border-default)" }}
            >
              <td style={{ padding: "8px 0" }}>
                <StatusIcon status={status} />
              </td>
              <td style={{ padding: 8 }}>{m.name}</td>
              <td
                style={{
                  padding: 8,
                  whiteSpace: "nowrap",
                  ...dateCellStyle(m, status),
                }}
              >
                {pickDisplayDate(m, status)}
              </td>
              {canEdit && (
                <td
                  style={{
                    padding: 8,
                    textAlign: "end",
                    whiteSpace: "nowrap",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onEdit(m)}
                    aria-label="Edit milestone"
                    title="Edit milestone"
                    style={{
                      padding: 4,
                      border: "none",
                      background: "transparent",
                      color: "var(--text-muted)",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PencilIcon />
                  </button>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/* ── Icons ───────────────────────────────────────────────── */

/** Tabler-style pencil. Stroke-only so it adopts `color` from parent. */
function PencilIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );
}

function StatusIcon({ status }: { status: Status }) {
  const p = getActiveIndicatorPalette();
  if (status === "met") {
    return (
      <svg width={14} height={14} viewBox="0 0 24 24" aria-label="met">
        <circle cx={12} cy={12} r={9} fill={p.completed} />
      </svg>
    );
  }
  if (status === "overdue") {
    return (
      <svg width={14} height={14} viewBox="0 0 24 24" aria-label="overdue">
        <circle cx={12} cy={12} r={9} fill={p.red} />
      </svg>
    );
  }
  // upcoming — target date is in the future (or unset). Green to signal
  // "on track" rather than grey, which read as "no data."
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" aria-label="upcoming">
      <circle cx={12} cy={12} r={9} fill={p.green} />
    </svg>
  );
}
